import { createContext, useCallback, useContext, useState } from 'react'

import { ValidationStatus } from 'types/shared'

export type GroupValidationContextType = {
  groupsStatus: Record<string, ValidationStatus>
  setActivityValidationStatus: (uuid: string, status: ValidationStatus) => void
  subscribeDependency: (
    dependency: string,
    uuid: string,
    callback: () => [string[], string]
  ) => void
  unsubscribeDependency: (dependency: string, uuid: string) => void
  getDependencyCallbacks: (uuid: string) => (() => [string[], string])[]
  hasSubscription: (uuid: string) => boolean
}

export const GroupValidationContext = createContext<GroupValidationContextType>(
  {
    groupsStatus: {},
    setActivityValidationStatus: () => {},
    subscribeDependency: () => {},
    unsubscribeDependency: () => {},
    getDependencyCallbacks: () => [],
    hasSubscription: () => false
  }
)

export const GroupValidationContextProvider = ({
  children
}: React.PropsWithChildren) => {
  const [groupsStatus, setGroupsStatus] = useState<
    Record<string, ValidationStatus>
  >({})

  const [dependencyList, setDependencyList] = useState<
    Record<string, Record<string, () => [string[], string]>>
  >({})

  const setActivityValidationStatus = useCallback(
    (uuid: string, status: ValidationStatus) => {
      setGroupsStatus((prev) => {
        if (prev[uuid] === status) {
          return prev
        }

        return {
          ...prev,
          [uuid]: status
        }
      })
    },
    [setGroupsStatus]
  )

  const subscribeDependency = (
    dependency: string,
    uuid: string,
    callback: () => [string[], string]
  ) => {
    setDependencyList((prev) => ({
      ...prev,
      [dependency]: {
        ...(prev[dependency] || {}),
        [uuid]: callback
      }
    }))
  }

  const unsubscribeDependency = (dependency: string, uuid: string) => {
    setDependencyList((prev) => {
      if (!prev[dependency]) return prev
      const { [uuid]: _removed, ...rest } = prev[dependency]
      return { ...prev, [dependency]: rest }
    })
  }

  const getDependencyCallbacks = (uuid: string) =>
    Object.entries(dependencyList[uuid] || {}).flatMap(
      ([, callback]) => callback
    )

  const hasSubscription = (uuid: string) =>
    Object.values(dependencyList).some((dependency) =>
      Object.keys(dependency).includes(uuid)
    )

  return (
    <GroupValidationContext.Provider
      value={{
        subscribeDependency,
        unsubscribeDependency,
        getDependencyCallbacks,
        hasSubscription,
        groupsStatus,
        setActivityValidationStatus
      }}
    >
      {children}
    </GroupValidationContext.Provider>
  )
}

export const useGroupValidationContext = () =>
  useContext(GroupValidationContext)
